import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

/**
 * パスワード再設定ページ /set-password?token=xxx
 * ログイン画面の「パスワードがわからない場合」で発行したリンク、または招待リンクのトークンで開く。
 */
export function SetPasswordPage() {
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const token = params.get("token") ?? null;

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError("リンクが無効です（token がありません）");
      return;
    }
    if (password !== passwordConfirm) {
      setError("パスワードが一致しません");
      return;
    }
    if (password.length < 8) {
      setError("パスワードは8文字以上にしてください");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.auth.setPassword({ token, password });
      setDone(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md border-border/80 bg-card/50 rounded-xl shadow-sm">
          <CardContent className="pt-6">
            <p className="text-destructive">リンクが無効です。パスワードがわからない場合はログイン画面の「パスワードがわからない場合」から再度リンクを発行してください。</p>
            <a href="/" className="mt-4 inline-block text-sm text-primary underline hover:no-underline">ログイン画面へ</a>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md border-border/80 bg-card/50 rounded-xl shadow-sm">
          <CardContent className="pt-6">
            <p className="text-muted-foreground">パスワードを設定しました。</p>
            <a href="/" className="mt-4 inline-block text-primary underline hover:no-underline">ログイン画面からログインする</a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md border-border/80 bg-card/50 rounded-xl shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">パスワードを再設定</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-muted-foreground mb-4">
            新しいパスワードを設定してください（8文字以上）。設定後、ログイン画面からメールアドレスとパスワードでログインできます。
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="set-password">新しいパスワード</Label>
              <Input
                id="set-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="rounded-lg mt-1"
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label htmlFor="set-password-confirm">パスワード（確認）</Label>
              <Input
                id="set-password-confirm"
                type="password"
                value={passwordConfirm}
                onChange={e => setPasswordConfirm(e.target.value)}
                className="rounded-lg mt-1"
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full rounded-lg" disabled={submitting}>
              {submitting ? "設定中…" : "パスワードを設定する"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
